import { z } from 'zod'

import {
  bsDateOrDashSchema,
  lenientList,
  moneySchema,
  searchTextParamSchema,
} from '@/schemas/common'
import { marketBreadthSchema } from '@/schemas/market'
import { tableQuerySchema, tablePageSchema } from '@/schemas/table'

/**
 * The four price tables: Live market, Stock prices, 52-week change and
 * Gainers & losers.
 *
 * One row shape serves the first two. The frames differ only in column order
 * and in whether the company name sits under the symbol, which is a table
 * concern — a second row type would let the same NABIL print carry two
 * different last prices depending on which page you opened.
 */

/**
 * The day's open/high/low/close, as numbers rather than `Money`.
 *
 * These four feed `DayRangeBar` and nothing else: they are positions on a
 * track, never figures a member reads. Every price the table actually prints
 * — LTP, previous close, the change — is `Money` below. Keeping the two apart
 * means the bar can do arithmetic on a float without that float ever reaching
 * a rupee column.
 */
export const dayRangeSchema = z.object({
  low: z.number(),
  high: z.number(),
  open: z.number(),
  /** `null` when the session has no published close yet — `DayRangeBar`
   *  already renders nothing rather than a dot in the wrong place. */
  close: z.number().nullable(),
})

export const priceRowSchema = z.object({
  symbol: z.string().min(1),
  name: z.string().min(1),
  /**
   * Up to five closes for the "5 days" cell. Its slope agrees with
   * `percentChange` — a green sparkline over a red change reads as a rendering
   * bug.
   *
   * Not exactly five: mm-be (services/market/live.py `SPARK_DAYS = 5`) sends
   * only the last five trading dates on which this scrip has a price, so a thin
   * trader carries 0–4 points, and `Sparkline` draws nothing under two. It is
   * decoration, so a malformed series empties this one cell rather than failing
   * the row — `.length(5)` here once failed every row and blanked the whole
   * Live Market and Stock Prices tables. `sparklineValues` already empties a
   * series with a non-decimal point before it gets here; this `.catch` is the
   * net under it, for a series with more than five.
   */
  sparkline: z
    .array(z.number())
    .max(5)
    .catch(({ issues, value }) => {
      console.warn('drew no sparkline for a malformed series', issues, value)
      return []
    }),
  range: dayRangeSchema,
  /** `null` when this symbol is absent from the movers list that carries the
   *  volume — a quiet scrip is legitimately absent from a bounded list. */
  volumeKitta: z.number().int().nonnegative().nullable(),
  ltp: moneySchema,
  percentChange: moneySchema,
})

/** The price rows as a list, so one unparseable row is dropped instead of failing the table. */
export const priceRowListSchema = lenientList(priceRowSchema, 'price')

/** The three segments of the breadth bar, used as a table filter. */
export const breadthFilterSchema = z.enum(['rose', 'fell', 'flat'])

export const liveMarketQuerySchema = tableQuerySchema.extend({
  // Live market no longer draws the 5-day column, so an old `?sortBy=sparkline` link falls
  // back to the default order instead of sorting by a column that is gone.
  sortBy: z
    .string()
    .min(1)
    .refine((id) => id !== 'sparkline')
    .optional()
    .catch(undefined),
  sector: z.string().min(1).optional(),
  breadth: breadthFilterSchema.optional(),
})

export const liveMarketPageSchema = tablePageSchema(priceRowSchema).extend({
  asOf: bsDateOrDashSchema,
  status: z.enum(['open', 'closed']),
  /** '—' when `MarketSummary.as_of` is null, which `displayTime` prints for it. */
  statusTime: z.union([
    z.string().regex(/^\d{1,2}:\d{2} [AP]M$/, 'Expected a clock time, e.g. "3:00 PM"'),
    z.literal('—'),
  ]),
  breadth: marketBreadthSchema,
  mitraSays: z.string().min(1),
})

export const stockPricesQuerySchema = tableQuerySchema.extend({
  query: searchTextParamSchema.optional(),
})

export const stockPricesPageSchema = tablePageSchema(priceRowSchema).extend({
  mitraSays: z.string().min(1),
})

/** Which end of the 52-week range the table is ordered against. */
export const week52SortSchema = z.enum(['nearest-high', 'nearest-low'])

export const week52RowSchema = z.object({
  symbol: z.string().min(1),
  name: z.string().min(1),
  ltp: moneySchema,
  high52: moneySchema,
  /** Both bounds are non-null on the wire, so every row carries the pair. */
  low52: moneySchema,
  /**
   * Whole percent, not two decimals: the frame prints "−72%", and a distance
   * from a yearly extreme is a rough fact about the range. `RangeBar` gets
   * `ltp`/`low52`/`high52` and does its own arithmetic, so nothing needs the
   * precision this field drops.
   *
   * `null` when mm-be sends no distance (the wire types it nullable); the table
   * prints a dash rather than a made-up 0%.
   */
  offHighPercent: moneySchema.nullable(),
  /** The same distance up from the low (positive), for the Low tab's bar. `null` as above. */
  offLowPercent: moneySchema.nullable(),
})

export const week52QuerySchema = tableQuerySchema.extend({
  sort: week52SortSchema,
  sector: z.string().min(1).optional(),
  /**
   * A symbol or company-name fragment. `/market/fifty-two-week/` takes no `q`, so
   * `pricesService.week52` filters the rows it already fetched in full.
   */
  query: searchTextParamSchema.optional(),
})

export const week52PageSchema = tablePageSchema(week52RowSchema).extend({
  intro: z.string().min(1),
  footnote: z.string().min(1),
  mitraSays: z.string().min(1),
})

/** "1 day" is served; "1 week" has no mm-be feed yet (`GAINERS_LOSERS_WEEK_AVAILABLE`). */
export const gainersLosersPeriodSchema = z.enum(['1d', '1w'])

/** The period and the one pager both columns share: page N of each, side by side. */
export const gainersLosersQuerySchema = tableQuerySchema
  .pick({ page: true, pageSize: true })
  .extend({ period: gainersLosersPeriodSchema.default('1d') })

export const glRowSchema = z.object({
  symbol: z.string().min(1),
  name: z.string().min(1),
  ltp: moneySchema,
  percentChange: moneySchema,
})

/**
 * `total` is the size of the whole move list, not of the ten drawn rows —
 * that is what "showing 10 of 62" counts. The two columns carry different
 * totals because 62 names rose and 31 fell.
 *
 * It comes from `/market/movers/`'s `meta.total`, which mm-be counts before it
 * slices, NOT from the number of rows this app asked for. Reading the fetch size
 * instead made both columns say "of 100" — `MOVERS_MAX_LIMIT` — on every session.
 */
export const glColumnSchema = z.object({
  rows: z.array(glRowSchema),
  total: z.number().int().nonnegative(),
})

export const gainersLosersSchema = z.object({
  gainers: glColumnSchema,
  losers: glColumnSchema,
  footnote: z.string().min(1),
  mitraSays: z.string().min(1),
})

/**
 * One session of a stock's history: the line's level plus everything the
 * Price history table prints beside it.
 *
 * `percentChange`, `volume` and `turnover` are the session's own recorded
 * figures, not derived here — the table used to compute its % chg against the
 * previous point on the page, which left the first row with no change and put a
 * gap's worth of days into the next one.
 */
export const priceHistoryPointSchema = z.object({
  /** ISO date of the session (the wire's `t`); printed through `formatBsDate`. */
  date: z.string().min(1),
  /** The session's last traded price: the level the sparkline draws. */
  value: moneySchema,
  /**
   * The session's four prices. Null for one the record never held: mm-be stores
   * that as 0 and NEPSE has no price of 0, so `mapPriceHistory` reads a zero as
   * the gap it is. (`close` falls back to the ltp for a live session with no
   * close yet, so it is null only when both are missing.)
   */
  open: moneySchema.nullable(),
  high: moneySchema.nullable(),
  low: moneySchema.nullable(),
  close: moneySchema.nullable(),
  /**
   * Against the previous close, as recorded for that session. This and the two
   * figures below arrived in mm-be 5f4ed8f, after the Overview chart first read
   * this series, and a deploy that lags it must still draw the line. So a
   * figure missing or unreadable reads as not reported, rather than dropping
   * its session. So does a negative volume: a broken row, not a count.
   */
  percentChange: moneySchema.nullable().catch(null),
  volume: z.number().int().nonnegative().nullable().catch(null),
  /** Null when nothing was recorded, which is not a session that traded nothing. */
  turnover: moneySchema.nullable().catch(null),
})

/**
 * The strip above the table, for the RANGE on screen. Each figure is null when
 * no session in the range carries it.
 *
 * `high` and `low` are the highest and lowest PRICE TOUCHED (each bar's own high
 * and low), not the highest and lowest close. The averages leave a session
 * without the figure out of both the sum and the count.
 */
export const priceHistorySummarySchema = z.object({
  high: moneySchema.nullable(),
  low: moneySchema.nullable(),
  /** Kitta per session. A decimal on the wire, so a `Money`, not a count. */
  averageVolume: moneySchema.nullable(),
  averageTurnover: moneySchema.nullable(),
})

export const priceHistorySchema = z.object({
  symbol: z.string().min(1),
  name: z.string().min(1),
  range: z.enum(['1W', '1M', '3M', '1Y', 'ALL']),
  /** Oldest first, as served. One session that will not parse is dropped, not the table. */
  points: lenientList(priceHistoryPointSchema, 'price history'),
  /**
   * Null when the response carries no readable summary (one from before mm-be
   * 5f4ed8f has none). The strip above the table is then left off.
   */
  summary: priceHistorySummarySchema.nullable().catch(null),
})

export type DayRange = z.infer<typeof dayRangeSchema>
export type PriceRow = z.infer<typeof priceRowSchema>
export type BreadthFilter = z.infer<typeof breadthFilterSchema>
export type LiveMarketQuery = z.infer<typeof liveMarketQuerySchema>
export type LiveMarketPage = z.infer<typeof liveMarketPageSchema>
export type StockPricesQuery = z.infer<typeof stockPricesQuerySchema>
export type StockPricesPage = z.infer<typeof stockPricesPageSchema>
export type Week52Sort = z.infer<typeof week52SortSchema>
export type Week52Row = z.infer<typeof week52RowSchema>
export type Week52Query = z.infer<typeof week52QuerySchema>
export type Week52Page = z.infer<typeof week52PageSchema>
export type GainersLosersPeriod = z.infer<typeof gainersLosersPeriodSchema>
export type GainersLosersQuery = z.infer<typeof gainersLosersQuerySchema>
export type GLRow = z.infer<typeof glRowSchema>
export type GLColumn = z.infer<typeof glColumnSchema>
export type GainersLosers = z.infer<typeof gainersLosersSchema>
export type PriceHistoryPoint = z.infer<typeof priceHistoryPointSchema>
export type PriceHistorySummary = z.infer<typeof priceHistorySummarySchema>
export type PriceHistory = z.infer<typeof priceHistorySchema>
